import { availableAsOf } from './bank-dates';
import { isCategoryHidden } from './catalog';
import { addDays } from './quarantine';
import { shuffleQuestionOptions } from './questions-utils';
import { hashtagsFor } from './social-tags';
// Question posts for social media and chat groups: which questions to share on a
// given day, and the words around them. Pure and stateless: the same date always
// gives the same picks, so "yesterday's answer" needs no stored list. Nothing here
// invents numbers ("only 3% get this right"): captions say only what is true.
export const APP_URL = 'https://brainfart.prasadkhake.com';
/** Our own accounts, in the order they are shown to people (the app's Follow us popup, the question pages, the structured data in index.html). */
export const SOCIAL_PROFILES = [
    { key: 'instagram', name: 'Instagram', url: 'https://www.instagram.com/brainfartquiz/' },
    { key: 'youtube', name: 'YouTube', url: 'https://www.youtube.com/@brainfartquiz' },
    { key: 'threads', name: 'Threads', url: 'https://www.threads.com/@brainfartquiz' },
    { key: 'bluesky', name: 'Bluesky', url: 'https://bsky.app/profile/brainfartquiz.bsky.social' },
];
export const OPTION_LETTERS = ['A', 'B', 'C', 'D'];
// Questions about India, for the share of posts that should lean that way.
const INDIA = /\b(india|indian|bollywood|cricket|ipl|isro|hindi|rupee|mumbai|delhi|kolkata|chennai|bengal|kerala|goa|tamil|telugu|punjab|ganga|himalaya|taj mahal|diwali|holi|ramayana|mahabharata|tagore|gandhi|nehru|tendulkar|kohli|rahman|raman)\b/i;
export const isIndiaLeaning = (q) => q.category === 'Indian Ads' || q.category === 'Cricket' || q.category === 'Indian Food' || q.category === 'Bollywood' || INDIA.test(`${q.prompt} ${q.theme ?? ''} ${String(q.correctAnswer)}`);
const hash = (s) => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    h ^= h >>> 15;
    h = Math.imul(h, 2246822507);
    h ^= h >>> 13;
    return h >>> 0;
};
// A brand-new question isn't posted until it has been live this many days (it also had a few days' notice before going
// live): a wrong answer in a video on six platforms costs far more than one in the app, so new questions get a little
// time in front of players (and their reports) first.
export const SOCIAL_COOL_OFF_DAYS = 3;
// Topics that stay out of social posts (category keys, see CATEGORIES). Empty on purpose: every topic is posted, no restrictions (Cocktails
// and TrueCrime were excluded until 2026-10-21 for reach and age-limit worries; the owner took them back in). If a platform ever objects
// to a topic, add its key here and the picker, the daily render and the video packs all leave it out.
export const SOCIAL_EXCLUDED_CATEGORIES = [];
// A topic isn't picked again for this many days, so a feed doesn't become five posts about the same thing (the India slot
// especially: Indian Ads is by far the biggest India-leaning pool). If nothing else qualifies, the rule gives way.
export const SOCIAL_TOPIC_COOLDOWN_DAYS = 4;
/**
 * A prompt that is only a quoted phrase ("Taste the Thunder") reads badly as a video or a card: it has no question in it.
 * A short question that ends in "?" is fine (What is "5G"?).
 */
export const isBareQuote = (prompt) => !/\?\s*$/.test(prompt) && prompt.replace(/["“”][^"“”]*["“”]/g, ' ').trim().split(/\s+/).filter(Boolean).length < 3;
/** Questions that make a good post: live that day and past their cooling-off, multiple choice, not too hard to enjoy at a glance. */
export function socialPool(questions, date) {
    const settled = addDays(date, -SOCIAL_COOL_OFF_DAYS);
    return questions.filter(q => q.type === 'multiple-choice' && availableAsOf(q, date) &&
        (!q.addedOn || q.addedOn <= settled) &&
        (q.difficulty === 'easy' || q.difficulty === 'medium') &&
        q.options?.length === 4 && q.prompt.length <= 160 &&
        !SOCIAL_EXCLUDED_CATEGORIES.includes(q.category) && !isCategoryHidden(q.category, date) && !isBareQuote(q.prompt));
}
/**
 * The mini quiz on the page the Instagram bio link opens (/q with no question in the address): five questions from five different
 * topics, the same all day. Same pool as the posts (good at a glance, past their cooling-off) but its own pick, so it never
 * depends on what was posted when.
 */
export function quickQuiz(questions, date, count = 5) {
    const ranked = socialPool(questions, date).sort((a, b) => hash(`${a.id}|quick|${date}`) - hash(`${b.id}|quick|${date}`));
    const topics = new Set();
    const picked = [];
    for (const q of ranked) {
        if (picked.length >= count)
            break;
        if (topics.has(q.category))
            continue;
        topics.add(q.category);
        picked.push(q);
    }
    // Easy ones first: the opening question is what a stranger judges the whole page by.
    return picked.sort((a, b) => Number(a.difficulty !== 'easy') - Number(b.difficulty !== 'easy'));
}
export const openingFor = (questionId) => (hash(`${questionId}|opening`) % 2 ? 'instant' : 'staggered');
export const SOCIAL_RAMP = [
    { from: '2026-10-12', count: 2, india: 1 },
    { from: '2026-10-26', count: 4, india: 1 },
    // From 2026-11-02 no slot is reserved for India-leaning questions: YouTube's last 28 days gave the UK and US about twice
    // India's views, and the owner prefers a non-India audience. India-leaning questions can still be picked on merit.
    { from: '2026-11-02', count: 4, india: 0 },
    { from: '2026-11-23', count: 6, india: 0 },
];
export const planFor = (date) => [...SOCIAL_RAMP].reverse().find(p => p.from <= date) ?? SOCIAL_RAMP[0];
// Post times (UTC) by videos per day: spread so India's morning and evening, Europe's day and the US evening each get one.
const SLOT_SETS = {
    1: ['13:30'], 2: ['02:00', '13:30'], 3: ['02:00', '07:30', '13:30'], 4: ['02:00', '07:30', '13:30', '23:00'],
    5: ['02:00', '07:30', '11:00', '13:30', '23:00'], 6: ['02:00', '05:00', '08:00', '11:30', '14:00', '23:00'],
};
export const slotsFor = (count) => SLOT_SETS[Math.min(6, Math.max(1, count))];
// From this date the weakest slot (07:30 UTC: index 0.69 over 8 posts in the first two weeks, against 4.47 for 13:30 and 1.48 for
// 02:00) is replaced by a UK evening one (18:30). 13:30 and 02:00 (US evening) stay. Days before it keep their old times.
export const RETIME_FROM = '2026-11-02';
const SLOT_SETS_UK_US = {
    1: ['13:30'], 2: ['02:00', '13:30'], 3: ['02:00', '13:30', '23:00'], 4: ['02:00', '13:30', '18:30', '23:00'],
    5: ['02:00', '11:00', '13:30', '18:30', '23:00'], 6: ['02:00', '11:00', '13:30', '16:00', '18:30', '23:00'],
};
/** The post times (UTC) for a day with `count` videos: the plan that was in force that day. */
export const slotsOn = (date, count) => date >= RETIME_FROM ? SLOT_SETS_UK_US[Math.min(6, Math.max(1, count))] : slotsFor(count);
/** "13:30" UTC -> "19:00" IST. */
export const istOf = (utc) => {
    const [h, m] = utc.split(':').map(Number);
    const total = (h * 60 + m + 330) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};
// The first day of the schedule. Days before it are picked with no history.
export const SOCIAL_EPOCH = '2026-10-12';
/**
 * The day's questions. For each date every eligible question gets a stable score
 * (a hash of its id and the date) and the lowest scores win, with India-leaning
 * slots filled first, no category twice in a day, and nothing that was posted in
 * the previous `noRepeatDays` days.
 *
 * "Posted" is worked out by walking forward from SOCIAL_EPOCH one day at a time,
 * so the answer for any date, including "yesterday's question" for tomorrow's
 * answer post, is exactly what was posted, with no list to store. That stays true
 * as the bank grows because each day's pool is the bank as of that day (bank.ts).
 */
export function pickSocialQuestions(questions, date, opts = {}) {
    const { noRepeatDays = 30, exclude } = opts;
    const start = date < SOCIAL_EPOCH ? date : SOCIAL_EPOCH;
    const history = []; // ids posted on each day so far, oldest first
    const topics = []; // the topics posted on each day so far
    let today = [];
    for (let day = start; day <= date; day = addDays(day, 1)) {
        const skip = new Set([...history.slice(-noRepeatDays).flat(), ...(exclude ?? [])]);
        const plan = planFor(day);
        const recentTopics = new Set(topics.slice(-SOCIAL_TOPIC_COOLDOWN_DAYS).flat());
        today = pickOne(questions, day, opts.count ?? plan.count, opts.india ?? plan.india, skip, recentTopics);
        history.push(today.map(q => q.id));
        topics.push(today.map(q => q.category));
    }
    return today;
}
function pickOne(questions, date, count, india, skip, recentTopics) {
    const ranked = socialPool(questions, date)
        .filter(q => !skip.has(q.id))
        .sort((a, b) => hash(`${a.id}|${date}`) - hash(`${b.id}|${date}`));
    const picked = [];
    const usedCategories = new Set();
    const take = (want, accept) => {
        for (const q of ranked) {
            if (picked.length >= count || want <= 0)
                break;
            if (picked.includes(q) || usedCategories.has(q.category) || !accept(q))
                continue;
            picked.push(q);
            usedCategories.add(q.category);
            want--;
        }
    };
    const fresh = (q) => !recentTopics.has(q.category);
    // India slots and the rest both prefer topics that haven't been posted lately; the rule only gives way if nothing else qualifies.
    take(Math.min(india, count), q => isIndiaLeaning(q) && fresh(q));
    take(count - picked.length, fresh);
    take(count - picked.length, () => true);
    return picked;
}
/** The options in the order the app shows them, with letters. */
export function lettered(q) {
    return shuffleQuestionOptions(q).options.map((text, i) => ({ letter: OPTION_LETTERS[i], text, correct: text === q.correctAnswer }));
}
export const CARD_SIZES = {
    og: { width: 1200, height: 630 },
    square: { width: 1080, height: 1080 },
    portrait: { width: 1080, height: 1350 },
};
/** The page that plays this one question (and sends people on to the app). */
export const questionLink = (id, { origin = APP_URL, source = 'q' } = {}) => `${origin}/q?id=${encodeURIComponent(id)}&source=${encodeURIComponent(source)}`;
/** The picture: the question with its options, or the answer revealed. */
export const cardUrl = (id, kind, size, origin = 'https://brainfart-server.prasadkhake.workers.dev') => `${origin}/og.png?kind=${kind}&id=${encodeURIComponent(id)}&size=${size}`;
// The hashtags for one platform (shared/src/social-tags.ts: how many, and which, depends on where it is posted).
const tags = (q, platform) => hashtagsFor(q, platform, isIndiaLeaning(q)).join(' ');
/**
 * What to write next to the question card. House voice: friendly quiz night, "topic" and "quiz" (never "category" or
 * "roast"), a clear next step on every platform, and nothing about how many people get it right.
 * Instagram has no clickable links in captions, so it points to the bio; LinkedIn is the founder's own profile, so "I".
 */
export function questionCaptions(q, links = {}) {
    const opts = lettered(q).map(o => `${o.letter}) ${o.text}`).join('\n');
    return {
        whatsapp: `*${q.prompt}*\n\n${opts}\n\nReply with A, B, C or D, then check your answer 👇\n${questionLink(q.id, { ...links, source: 'wa' })}`,
        instagram: `${q.prompt}\n\n${opts}\n\nComment A, B, C or D 👇 The answer drops tomorrow.\nPlay free: link in bio.\n\n${tags(q, 'instagram')}`,
        linkedin: `Quick quiz break: ${q.prompt}\n\n${opts}\n\nYour answer in the comments, and I'll post it tomorrow.\nPlay free: ${questionLink(q.id, { ...links, source: 'li' })}\n\n${tags(q, 'linkedin')}`,
    };
}
/** The follow-up the next day. */
export function answerCaptions(q) {
    const right = lettered(q).find(o => o.correct);
    const line = `Yesterday's question: ${q.prompt}\n\nAnswer: ${right.letter}) ${right.text}`;
    return {
        whatsapp: `${line}\n\nGot it? Try today's Daily Quiz: ${APP_URL}/?daily=1&source=wa`,
        instagram: `${line}\n\nDid you get it? A new question is up today.\nPlay free: link in bio.\n\n${tags(q, 'instagram')}`,
        linkedin: `${line}\n\nA new question every day. Play free: ${APP_URL}/?daily=1&source=li\n\n${tags(q, 'linkedin')}`,
    };
}
/**
 * Captions for the quiz video (scripts/video.mjs). The video already says "Comment A, B, C or D", so the caption repeats
 * the question for people who read first and never reveals the answer. Shorts titles are short; TikTok captions are
 * shorter still.
 */
export function videoCaptions(q, links = {}) {
    const opts = lettered(q).map(o => `${o.letter}) ${o.text}`).join('\n');
    return {
        instagram: `${q.prompt}\n\nComment A, B, C or D 👇 The answer's in the video.\nPlay free: link in bio.\n\n${tags(q, 'instagram')}`,
        tiktok: `${q.prompt} Comment A, B, C or D 👇 ${tags(q, 'tiktok')}`,
        youtubeTitle: `${q.prompt.length > 84 ? `${q.prompt.slice(0, 83)}…` : q.prompt} #shorts`,
        youtubeDescription: `${q.prompt}\n\n${opts}\n\nThe answer is in the video. Play free: ${questionLink(q.id, { ...links, source: 'yt' })}\n\n${tags(q, 'youtube')} #shorts`,
        linkedin: `Quick quiz break (watch to the end for the answer): ${q.prompt}\n\nYour answer in the comments.\nPlay free: ${questionLink(q.id, { ...links, source: 'li' })}\n\n${tags(q, 'linkedin')}`,
        threads: threadsText(q, opts, links),
        bluesky: blueskyText(q, links),
    };
}
// Threads allows 500 characters: the options go in when they fit, the question and a link always do. One hashtag, last: on
// Threads it becomes the post's topic.
function threadsText(q, opts, links) {
    const tail = `Reply with your answer. Play free: ${questionLink(q.id, { ...links, source: 'th' })}\n\n${tags(q, 'threads')}`;
    const full = `${q.prompt}\n\n${opts}\n\n${tail}`;
    return full.length <= 480 ? full : `${q.prompt}\n\n${tail}`;
}
// Bluesky allows 300 characters and the card picture carries the options, so: the question, a link and (the adapter makes
// them clickable) a couple of tags. The question gives way first when it is too long.
function blueskyText(q, links) {
    const tail = `\n\nPlay free: ${questionLink(q.id, { ...links, source: 'bs' })}\n\n${tags(q, 'bluesky')}`;
    const max = 300 - tail.length;
    return `${q.prompt.length > max ? `${q.prompt.slice(0, max - 1)}…` : q.prompt}${tail}`;
}
// ---------------------------------------------------------------------------------------------------------------------
// The day's posting plan: which question goes out when, and where.
// ---------------------------------------------------------------------------------------------------------------------
/** Where videos are posted automatically. TikTok stays manual until its audit passes (see the strategy notes). */
export const SOCIAL_PLATFORMS = ['instagram', 'youtube', 'threads', 'bluesky', 'linkedin'];
/** The day's posts: its picks (stateless, see pickSocialQuestions) paired with the post times for that many videos a day. */
export function dayPlan(questions, date, opts = {}) {
    const picks = pickSocialQuestions(questions, date, opts);
    const slots = slotsOn(date, picks.length);
    return picks.map((question, i) => ({
        date,
        slot: slots[i],
        scheduledAt: Date.parse(`${date}T${slots[i]}:00Z`),
        question,
        first: i === 0,
        // LinkedIn is the founder's own profile: one video a day reads as a post, more reads as spam.
        platforms: SOCIAL_PLATFORMS.filter(p => p !== 'linkedin' || i === 0),
    }));
}
